// angular
import { CommonModule, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
// components
import { ICardProperty } from '@app/core/components/card-property/card-property.component';
import { CardTitleComponent } from '@app/core/components/card-title/card-title.component';
import { ConfirmationDialogComponent } from '@app/core/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ItemPricesComponent } from '../item-prices/item-prices.component';
// services
import { DialogService } from '@app/core/shared/services/dialog.service';
import { FavoritesService } from '@app/+profile/profile-favorites/shared/favorites.service';
import { StorageService } from '@app/shared/services/storage.service';
// interfaces
import {
  IProduct,
  PriceType,
} from '@app/+products/shared/interfaces/product.interface';
import { ICardTitle } from '@app/shared/interfaces/card-title.interface';
import { II18n } from '@app/shared/interfaces/i18n.interface';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
// pipes & environment
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';
import { environment } from '@environments/environment';

@Component({
  selector: 'pwj-product-card',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    CardTitleComponent,
    TranslateModule,
    TranslateDataPipe,
    ImageSourcePipe,
    ItemPricesComponent,
    RouterLink,
  ],
  templateUrl: './product-card.component.html',
  styleUrl: './product-card.component.scss',
  host: {
    '[class.product-card-host--compact]': 'compact()',
  },
})
export class ProductCardComponent implements AfterViewInit {
  public panelUrl: string = environment.baseUrl.panelUrl;
  public baseUrl: string = environment.baseUrl.base;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;

  public cardProperties!: ICardProperty[];

  productCardData = input.required<IProduct>();
  simpleBorder = input<boolean>(false);
  compact = input<boolean>(false);
  isFavoriteMode = input<boolean>(false);

  readonly isIntersecting = signal(false);
  readonly scrollIdle = signal(true);
  readonly marqueeContextActive = computed(
    () => this.isIntersecting() && this.scrollIdle(),
  );

  readonly cardTitleData = computed<ICardTitle>(() => {
    const isCompact = this.compact();

    return {
      title: this.productCardData()?.title as II18n,
      titleClasses: isCompact
        ? '!text-lg sm:!text-xl md:!text-2xl lg:!text-3xl'
        : '!text-4xl',
      summarizeTitle: false,
      titleTruncate: isCompact,
      titleMarquee: isCompact
        ? undefined
        : { contextActive: this.marqueeContextActive() },
      subTitle: this.productCardData()?.category_type?.title as II18n,
      subTitleUrl: null,
      subTitleClasses: isCompact
        ? '[&>span]:!text-base sm:[&>span]:!text-lg md:[&>span]:!text-xl lg:[&>span]:!text-2xl'
        : '[&>span]:!text-3xl',
      summarizeSubTitle: !isCompact,
      subTitleTruncate: isCompact,
      summarizeSubTitleLength: 52,
      image: {
        src:
          this.productCardData()?.business?.logo?.cropped_url?.toLowerCase() ||
          this.productCardData()?.business?.logo?.url,
        alt: this.productCardData()?.business?.title,
        width: isCompact ? '24' : '32',
        height: isCompact ? '24' : '32',
      },
      imgClasses: isCompact ? '!w-24 !h-24' : '!w-32 !h-32',
    };
  });

  private readonly dialogService = inject(DialogService);
  private readonly favoriteService = inject(FavoritesService);
  private readonly storageService = inject(StorageService);
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  private scrollTimer?: ReturnType<typeof setTimeout>;
  private intersectionObserver?: IntersectionObserver;

  constructor() {
    this.currentCurrency =
      this.storageService.getCurrency() as keyof PriceType;
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.isIntersecting.set(entry.isIntersecting);
      },
      { threshold: 0.45 },
    );
    this.intersectionObserver.observe(this.hostEl.nativeElement);

    const onScroll = () => {
      this.scrollIdle.set(false);
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.scrollIdle.set(true);
      }, 220);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('scroll', onScroll);
      this.intersectionObserver?.disconnect();
      clearTimeout(this.scrollTimer);
    });
  }

  public removeProductFromFavorite(event: MouseEvent) {
    event.stopPropagation();

    const dialogRef = this.dialogService.open(ConfirmationDialogComponent, {
      width: '26em',
      height: '10em',
      data: { title: 'profile.favorites.confirmDeleteProductFromFavorites' },
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res && this.productCardData()?._id) {
        this.favoriteService.removeOneFavoriteItem(
          this.productCardData()?._id as string
        );
      }
    });
  }
}
